/**
 * Progress Tracker Utility
 * 
 * Utilities for tracking training progress and checking unlock status.
 * Uses localStorage to persist progress across sessions.
 */

export interface TrainingProgress {
  completed: string[];
  progress: number;
  lastUpdated: string;
  /** When true, trainee clicked "Mark Training as Completed"; used for unlock even if section count changed */
  markedComplete?: boolean;
}

/**
 * Get progress data for a specific training from localStorage (trainee-specific)
 * 
 * @param trainingId - The training ID ('training-1' or 'training-2')
 * @param traineeId - The trainee ID (default: 'default')
 * @returns Progress data or null if not found
 */
export function getTrainingProgress(trainingId: string, traineeId: string = 'default'): TrainingProgress | null {
  if (typeof window === 'undefined') return null;

  try {
    const saved = localStorage.getItem(`training-progress-${traineeId}-${trainingId}`);
    if (!saved) return null;

    const parsed = JSON.parse(saved);
    return {
      completed: parsed.completed || [],
      progress: parsed.progress || 0,
      lastUpdated: parsed.lastUpdated || new Date().toISOString(),
      markedComplete: parsed.markedComplete === true
    };
  } catch (error) {
    console.error(`Failed to load progress for ${traineeId}-${trainingId}:`, error);
    return null;
  }
}

/**
 * Get the number of completed sections for a training (trainee-specific)
 * 
 * @param trainingId - The training ID ('training-1' or 'training-2')
 * @param traineeId - The trainee ID (default: 'default')
 * @returns Number of completed sections
 */
export function getCompletedSectionsCount(trainingId: string, traineeId: string = 'default'): number {
  const progress = getTrainingProgress(trainingId, traineeId);
  return progress ? progress.completed.length : 0;
}

/**
 * Get progress percentage for a training (trainee-specific)
 * 
 * @param trainingId - The training ID ('training-1' or 'training-2')
 * @param totalSections - Total number of sections in the training
 * @param traineeId - The trainee ID (default: 'default')
 * @returns Progress percentage (0-100)
 */
export function getProgressPercentage(trainingId: string, totalSections: number, traineeId: string = 'default'): number {
  if (totalSections === 0) return 0;
  
  const completed = getCompletedSectionsCount(trainingId, traineeId);
  return Math.round((completed / totalSections) * 100);
}

/**
 * Check if Training 1 is complete (trainee-specific)
 * A training is considered complete if all sections are marked as completed.
 * 
 * @param totalSections - Total number of sections in Training 1
 * @param traineeId - The trainee ID (default: 'default')
 * @returns true if Training 1 is complete, false otherwise
 */
export function isTraining1Complete(totalSections: number, traineeId: string = 'default'): boolean {
  if (totalSections === 0) return false;
  
  const progress = getTrainingProgress('training-1', traineeId);
  if (!progress) return false;
  
  return progress.completed.length >= totalSections;
}

/**
 * Check if Training 2 is unlocked (trainee-specific)
 * Training 2 is unlocked when Training 1 is complete.
 */
export function isTraining2Unlocked(training1TotalSections: number, traineeId: string = 'default'): boolean {
  return isTraining1Complete(training1TotalSections, traineeId);
}

/**
 * Check if Training 2 is complete (trainee-specific)
 */
export function isTraining2Complete(totalSections: number, traineeId: string = 'default'): boolean {
  if (totalSections === 0) return false;
  const progress = getTrainingProgress('training-2', traineeId);
  if (!progress) return false;
  return progress.completed.length >= totalSections;
}

/**
 * Check if Training 3 is unlocked (trainee-specific)
 * Training 3 is unlocked when Training 2 is complete.
 */
export function isTraining3Unlocked(training2TotalSections: number, traineeId: string = 'default'): boolean {
  return isTraining2Complete(training2TotalSections, traineeId);
}

/**
 * Check if Training 3 is complete (trainee-specific)
 */
export function isTraining3Complete(totalSections: number, traineeId: string = 'default'): boolean {
  if (totalSections === 0) return false;
  const progress = getTrainingProgress('training-3', traineeId);
  if (!progress) return false;
  return progress.completed.length >= totalSections;
}

/**
 * Check if Training 4 is unlocked (trainee-specific)
 * Training 4 is unlocked when Training 3 is complete.
 */
export function isTraining4Unlocked(training3TotalSections: number, traineeId: string = 'default'): boolean {
  return isTraining3Complete(training3TotalSections, traineeId);
}

/**
 * Check if Training 4 is complete (trainee-specific).
 * Complete if: marked "Mark as completed" OR completed sections >= total (handles section count changes).
 */
export function isTraining4Complete(totalSections: number, traineeId: string = 'default'): boolean {
  const progress = getTrainingProgress('training-4', traineeId);
  if (!progress) return false;
  if (progress.markedComplete === true) return true;
  if (totalSections === 0) return false;
  return progress.completed.length >= totalSections;
}

/**
 * Check if Training 5 is unlocked (trainee-specific).
 * Unlocked when Training 4 is complete for this trainee OR for 'default' (legacy completions).
 */
export function isTraining5Unlocked(training4TotalSections: number, traineeId: string = 'default'): boolean {
  return isTraining4Complete(training4TotalSections, traineeId) || isTraining4Complete(training4TotalSections, 'default');
}

/**
 * Check if Training 5 is complete (trainee-specific).
 * Complete if: marked "Mark as completed" OR completed sections >= total (handles section count changes).
 */
export function isTraining5Complete(totalSections: number, traineeId: string = 'default'): boolean {
  const progress = getTrainingProgress('training-5', traineeId);
  if (!progress) return false;
  if (progress.markedComplete === true) return true;
  if (totalSections === 0) return false;
  return progress.completed.length >= totalSections;
}

/**
 * Check if Training 6 is unlocked (trainee-specific).
 * Unlocked when Training 5 is complete for this trainee OR for 'default' (legacy completions).
 */
export function isTraining6Unlocked(training5TotalSections: number, traineeId: string = 'default'): boolean {
  return isTraining5Complete(training5TotalSections, traineeId) || isTraining5Complete(training5TotalSections, 'default');
}

/**
 * Check if Training 6 is complete (trainee-specific).
 * Complete if: marked "Mark as completed" OR completed sections >= total (handles section count changes).
 */
export function isTraining6Complete(totalSections: number, traineeId: string = 'default'): boolean {
  const progress = getTrainingProgress('training-6', traineeId);
  if (!progress) return false;
  if (progress.markedComplete === true) return true;
  if (totalSections === 0) return false;
  return progress.completed.length >= totalSections;
}

/**
 * Check if Training 7 is unlocked (trainee-specific).
 * Unlocked when Training 6 is complete for this trainee OR for 'default' (legacy completions).
 */
export function isTraining7Unlocked(training6TotalSections: number, traineeId: string = 'default'): boolean {
  return isTraining6Complete(training6TotalSections, traineeId) || isTraining6Complete(training6TotalSections, 'default');
}

/**
 * Check if Training 7 is complete (trainee-specific).
 * Complete if: marked "Mark as completed" OR completed sections >= total (handles section count changes).
 */
export function isTraining7Complete(totalSections: number, traineeId: string = 'default'): boolean {
  const progress = getTrainingProgress('training-7', traineeId);
  if (!progress) return false;
  if (progress.markedComplete === true) return true;
  if (totalSections === 0) return false;
  return progress.completed.length >= totalSections;
}

/**
 * Check if Training 8 is unlocked (trainee-specific).
 * Unlocked when Training 7 is complete for this trainee OR for 'default' (legacy completions).
 */
export function isTraining8Unlocked(training7TotalSections: number, traineeId: string = 'default'): boolean {
  return isTraining7Complete(training7TotalSections, traineeId) || isTraining7Complete(training7TotalSections, 'default');
}

/**
 * Get training status (locked/unlocked, progress, etc.) - trainee-specific
 *
 * @param trainingNumber - Training number (1, 2, 3, 4, 5, 6, 7, or 8)
 * @param previousTotalSections - Total sections in the previous training (Training 1 total for Training 2, etc.; ignored for Training 1)
 * @param trainingTotalSections - Total sections in the requested training
 * @param traineeId - The trainee ID (default: 'default')
 */
export function getTrainingStatus(
  trainingNumber: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8,
  previousTotalSections: number,
  trainingTotalSections: number,
  traineeId: string = 'default'
) {
  const trainingId = `training-${trainingNumber}`;
  const progress = getTrainingProgress(trainingId, traineeId);
  const progressPercentage = getProgressPercentage(trainingId, trainingTotalSections, traineeId);

  let isLocked = false;
  if (trainingNumber === 2) {
    isLocked = !isTraining2Unlocked(previousTotalSections, traineeId);
  } else if (trainingNumber === 3) {
    isLocked = !isTraining3Unlocked(previousTotalSections, traineeId);
  } else if (trainingNumber === 4) {
    isLocked = !isTraining4Unlocked(previousTotalSections, traineeId);
  } else if (trainingNumber === 5) {
    isLocked = !isTraining5Unlocked(previousTotalSections, traineeId);
  } else if (trainingNumber === 6) {
    isLocked = !isTraining6Unlocked(previousTotalSections, traineeId);
  } else if (trainingNumber === 7) {
    isLocked = !isTraining7Unlocked(previousTotalSections, traineeId);
  } else if (trainingNumber === 8) {
    isLocked = !isTraining8Unlocked(previousTotalSections, traineeId);
  }

  const isComplete = progress
    ? progress.completed.length >= trainingTotalSections
    : false;

  return {
    trainingId,
    isLocked,
    isComplete,
    progress: progressPercentage,
    completedSections: progress?.completed.length || 0,
    totalSections: trainingTotalSections,
    lastUpdated: progress?.lastUpdated || null
  };
}
